import { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import { ReactComponent as KeyIcon } from '../../assets/Icons/adornments/Key.svg';
import PopupHeader from "./PopupHeader";
import PopupFooter from "./PopupFooter";
import InputField from "../Form/InputField";
import { useChangeUserPasswordAuthenticationProtectedChangePasswordPost } from "../../api/generated/fastAPI";
import ModalBase from "./ModalBase";

export interface ChangePWForm {
    newPassword: string;
    confirmPassword: string;
}

const defaultForm: ChangePWForm = {
    newPassword: '',
    confirmPassword: '',
}

export default function ChangePasswordModal({
    handleCloseMenu,
    setPopupMessage,
}: {
    handleCloseMenu?: () => void,
    setPopupMessage: (message: string, isSuccess: boolean) => void,
}) {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const [ form, setForm ] = useState<ChangePWForm>(defaultForm);
    const changePWMutation = useChangeUserPasswordAuthenticationProtectedChangePasswordPost();
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isDisabled, setIsDisabled ] = useState(false);

    const handleClose = () => {
        setOpen(false);
        setForm(defaultForm);
        handleCloseMenu && setTimeout(() => handleCloseMenu(), 0);
    }

    useEffect(() => {
        if (!form.newPassword || !form.confirmPassword) {
            setIsDisabled(true);
        } else if (form.newPassword !== form.confirmPassword) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [form]);

    const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        setForm({
            ...form,
            [name]: value,
        })
    }

    const handleSubmit = async (e: MouseEvent<HTMLButtonElement>, pwForm: ChangePWForm) => {
        e.preventDefault();

        setIsLoading(true);
        setIsDisabled(true);

        try {
            await changePWMutation.mutateAsync({ data: { new_password: pwForm.newPassword } });
            setPopupMessage(`You've successfully changed your password.`, true);
            handleClose();
        } catch (err:any) {
            console.log(err);
            setPopupMessage('There was an error changing your password. Please try again.', false);
        }

        setIsLoading(false);
        setIsDisabled(false);
    }

    return (
        <div>
            <button onClick={handleOpen} className={`w-full px-5 py-3  text-left font-medium flex justify-between items-center gap-3 w-full h-full`}>
                <div className='flex justify-start items-center gap-3'>
                    <div className='w-[18px] h-[18px]'><KeyIcon /></div>
                    <p className='text-nowrap'>Change Password</p>
                </div>
            </button>
            <ModalBase
                open={open}
                modalId="change-password-popup"
            >
                <div className={`bg-white rounded-lg w-full max-w-[400px] mx-auto popup-max-height`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'>
                        <PopupHeader label="Change Password" closeFn={handleClose} hasOutline={false}/>

                        <div className="w-full flex flex-col justify-start items-stretch gap-8 p-6">
                            <div className="w-full">
                                <InputField 
                                    label="New Password"
                                    type='password'
                                    id="newPassword"
                                    name="newPassword"
                                    value={form.newPassword}
                                    handleChange={handleInput}
                                    isRequired
                                />
                            </div>
                            <div className="w-full">
                                <InputField 
                                    label="Confirm Password"
                                    type='password'
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={form.confirmPassword}
                                    handleChange={handleInput}
                                    isRequired
                                />
                            </div>
                        </div>

                        <PopupFooter 
                            buttons={[
                                {
                                    label: 'Cancel',
                                    type: 'tertiary',
                                    styling: 'outline',
                                    id: 'cancel-pw-change',
                                    action: handleClose,
                                },
                                {
                                    label: 'Save New Password',
                                    type: isDisabled ? 'disabled' : 'primary',
                                    styling: 'solid',
                                    id: 'save-pw-btn',
                                    action: async (e: MouseEvent<HTMLButtonElement>) => handleSubmit(e, form),
                                    isLoading,
                                }
                            ]}
                            hasOutline={false}
                        />
                    </div>
                </div>
            </ModalBase>
        </div>
    )
}